// SlideOver — edge drawer on the React Aria foundation (ADR-029), per
// COMPONENTS §Modal vs SlideOver. The default for create/edit forms: the user
// keeps the surrounding context (the list they were viewing) without losing
// their place. Modals interrupt; SlideOvers extend.
//
// React Aria owns focus trap + focus return + Escape + backdrop close + body
// scroll lock + dialog semantics (it isolates the modal via aria-hidden on the
// rest of the page — more robust than aria-modal). We own 100% of the markup +
// tokens. Form state is NOT reset on close — values persist until explicit
// cancel or successful submit (the "discard changes?" prompt is a future
// addition for when the first edit-mode form ships).
//
// `side` is LOGICAL ('start' | 'end'), so the drawer flips under dir="rtl":
// 'end' (default) = create/edit forms; 'start' = nav drawers anchored under a
// start-side trigger (the hamburger).
//
// The peek's phone sheet passes its whole panel as the `face` (PeekSheet): the
// drawer keeps the frame, the panel brings its own header, body and footer.
//
// The overlay is sized to the VISIBLE screen (the foundation's
// `--visual-viewport-height`): on a phone the drawer's footer (Save) sits above
// the keyboard and the browser's bars, never under them. The face is unchanged
// (ADR-080 §12 — drawers stay drawers).

import { useId, type ReactElement, type ReactNode } from 'react';
import { ModalOverlay, Modal as AriaModal, Dialog, Heading } from 'react-aria-components';
import { icons } from './icons.js';
import { useTranslation } from 'react-i18next';
import { IconButton } from './IconButton.js';

interface SlideOverFrame {
  open: boolean;
  onClose: () => void;
  /** Tailwind max-w-* for panel width. Default max-w-lg. */
  maxWidth?: string;
  /** Which inline edge the panel slides in from (logical — flips under RTL).
   *  Default 'end' (create/edit forms); 'start' for navigation drawers. */
  side?: 'start' | 'end';
}

/** The drawer's own face — the title row (title + close X), the scrolling body, the footer. */
interface SlideOverStandard extends SlideOverFrame {
  title: string;
  description?: string;
  children: ReactNode;
  /** Footer slot — Cancel (secondary, start) → Submit (primary, end). */
  footer?: ReactNode;
  face?: never;
}

/** A caller-composed face in place of the title row · body · footer — the peek's
 *  panel on a phone (`PeekSheet`): its header is the same identity lockup as the
 *  wide panel and carries the `Heading slot="title"` that names the dialog, its
 *  close and its own scroll body. The drawer keeps the frame — scrim, edge slide,
 *  focus trap/return, Escape. */
interface SlideOverFace extends SlideOverFrame {
  face: ReactElement;
  title?: never;
  description?: never;
  children?: never;
  footer?: never;
}

export type SlideOverProps = SlideOverStandard | SlideOverFace;

export function SlideOver(props: SlideOverProps) {
  const { open, onClose, maxWidth = 'max-w-lg', side = 'end' } = props;
  const descId = useId();
  const { t } = useTranslation('common');

  return (
    <ModalOverlay
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      isDismissable
      className={`fixed inset-x-0 top-0 h-[var(--visual-viewport-height)] z-overlay flex bg-[var(--color-overlay)] data-[entering]:o-scrim-in data-[exiting]:o-scrim-out ${side === 'start' ? 'justify-start' : 'justify-end'}`}
    >
      <AriaModal
        // `--drawer-from` is the off-screen start offset; it flips under RTL so
        // the panel always slides in from its on-screen logical edge.
        className={`w-full ${maxWidth} h-full bg-[var(--color-bg-overlay)] text-[var(--color-text-primary)] shadow-modal ${side === 'start' ? 'border-e [--drawer-from:-100%] rtl:[--drawer-from:100%]' : 'border-s [--drawer-from:100%] rtl:[--drawer-from:-100%]'} border-[var(--color-border)] data-[entering]:o-drawer-in data-[exiting]:o-drawer-out`}
      >
        <Dialog
          aria-describedby={props.face === undefined && props.description ? descId : undefined}
          className="flex h-full flex-col outline-none"
        >
          {props.face !== undefined ? (
            props.face
          ) : (
            <>
              <header className="flex items-center justify-between gap-4 px-5 py-4 border-b border-[var(--color-border)]">
                <Heading slot="title" className="min-w-0 wrap-anywhere text-base font-semibold">
                  {props.title}
                </Heading>
                <IconButton aria-label={t('actions.close')} onClick={onClose} variant="ghost">
                  <icons.close aria-hidden size={18} />
                </IconButton>
              </header>
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {props.description && (
                  <p id={descId} className="text-sm text-[var(--color-text-secondary)] mb-3">
                    {props.description}
                  </p>
                )}
                {props.children}
              </div>
              {props.footer && (
                <footer className="px-5 py-3 border-t border-[var(--color-border)] flex justify-end gap-2 bg-[var(--color-bg-overlay)]">
                  {props.footer}
                </footer>
              )}
            </>
          )}
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  );
}
